/** Orchestrates all debug overlay modules, routing per-flag updates and handling the F1 master toggle. */
import Phaser from 'phaser';
import { Enemy } from '../entities/Enemy';
import { DebugConfig } from './DebugConfig';
import { EnemyBarsOverlay } from './overlays/EnemyBarsOverlay';
import { DetectionConeOverlay } from './overlays/DetectionConeOverlay';
import { PathOverlay } from './overlays/PathOverlay';
import { SearchMemoryOverlay } from './overlays/SearchMemoryOverlay';
import { StateLabelsOverlay } from './overlays/StateLabelsOverlay';
import { InspectorOverlay } from './overlays/InspectorOverlay';
import { OffscreenOverlay } from './overlays/OffscreenOverlay';
import { LegendOverlay } from './overlays/LegendOverlay';
import { DangerOverlay } from './overlays/DangerOverlay';
import { EnemySkillOverlay } from './overlays/EnemySkillOverlay';
/** Manages the full suite of debug overlays; call update() each frame from GameScene. */
export class DebugOverlayManager {
  private graphics: Phaser.GameObjects.Graphics;
  private toggleKey: Phaser.Input.Keyboard.Key;

  // Overlay modules
  private bars: EnemyBarsOverlay;
  private cones: DetectionConeOverlay;
  private paths: PathOverlay;
  private searchMemory: SearchMemoryOverlay;
  private stateLabels: StateLabelsOverlay;
  private inspector: InspectorOverlay;
  private offscreen: OffscreenOverlay;
  private legend: LegendOverlay;
  private danger: DangerOverlay;
  private enemySkills: EnemySkillOverlay;

  constructor(scene: Phaser.Scene, groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(2000);

    this.bars = new EnemyBarsOverlay();
    this.cones = new DetectionConeOverlay(groundLayer);
    this.paths = new PathOverlay();
    this.searchMemory = new SearchMemoryOverlay();
    this.stateLabels = new StateLabelsOverlay(scene);
    this.inspector = new InspectorOverlay(scene);
    this.offscreen = new OffscreenOverlay(scene);
    this.legend = new LegendOverlay(scene);
    this.danger = new DangerOverlay(scene);
    this.enemySkills = new EnemySkillOverlay(scene);

    this.toggleKey = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.F1);
    this.toggleKey.on('down', () => {
      DebugConfig.setEnabled(!DebugConfig.enabled);
      if (!DebugConfig.enabled) {
        this.clearAll();
      }
    });
  }

  /** Redraw all active overlays for the current frame using the provided enemy list. */
  update(enemies: readonly Enemy[]): void {
    if (!DebugConfig.enabled || !DebugConfig.anyOverlay) {
      this.graphics.clear();
      this.stateLabels.clear();
      this.inspector.clear();
      this.offscreen.clear();
      this.legend.clear();
      this.danger.clear();
      this.enemySkills.clear();
      return;
    }

    this.graphics.clear();

    // Graphics-based overlays (write to shared graphics)
    this.bars.draw(this.graphics, enemies);

    if (DebugConfig.flags.pathfindingLines) {
      this.paths.draw(this.graphics, enemies);
    }
    if (DebugConfig.flags.visionCones) {
      this.cones.draw(this.graphics, enemies);
    }
    if (DebugConfig.flags.searchMemoryMarkers) {
      this.searchMemory.draw(this.graphics, enemies);
    }

    // Text/sprite-based overlays (manage their own objects)
    if (DebugConfig.flags.aiStateLabels) {
      this.stateLabels.draw(this.graphics, enemies);
    } else {
      this.stateLabels.clear();
    }

    if (DebugConfig.flags.inspector) {
      this.inspector.draw(this.graphics, enemies);
    } else {
      this.inspector.clear();
    }

    if (DebugConfig.flags.offscreenIndicators) {
      this.offscreen.draw(this.graphics, enemies);
    } else {
      this.offscreen.clear();
    }

    if (DebugConfig.flags.legend) {
      this.legend.draw(this.graphics);
    } else {
      this.legend.clear();
    }

    if (DebugConfig.flags.dangerOverlay) {
      this.danger.draw(this.graphics, enemies);
    } else {
      this.danger.clear();
    }

    if (DebugConfig.flags.enemySkillOverlay) {
      this.enemySkills.draw(this.graphics, enemies);
    } else {
      this.enemySkills.clear();
    }
  }

  private clearAll(): void {
    this.graphics.clear();
    this.stateLabels.clear();
    this.inspector.clear();
    this.offscreen.clear();
    this.legend.clear();
    this.danger.clear();
    this.enemySkills.clear();
  }

  /** Destroy the shared graphics object and all overlay modules. */
  destroy(): void {
    this.graphics.destroy();
    this.stateLabels.destroy();
    this.inspector.destroy();
    this.offscreen.destroy();
    this.legend.destroy();
    this.danger.destroy();
    this.enemySkills.destroy();
  }
}
